import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, startViewTransition } from '@theme/utilities';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  };

  /**
   * Updates the section
   */
  #updateSection() {
    const viewTransition = !this.closest('dialog');

    if (viewTransition) {
      startViewTransition(() => sectionRenderer.renderSection(this.sectionId), ['product-grid']);
    } else {
      sectionRenderer.renderSection(this.sectionId);
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  /** @type {string} */
  currency;
  /** @type {string} */
  moneyFormat;
  /** @type {(() => void) | undefined} */
  #rangeSliderCleanup;
  /** @type {number | undefined} */
  #syncFrameId;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.addEventListener('input', this.#onInput);
    this.currency = this.dataset.currency ?? 'USD';
    this.moneyFormat = this.#extractMoneyPlaceholder(this.dataset.moneyFormat ?? '{{amount}}');
    this.#scheduleSliderSync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    this.removeEventListener('input', this.#onInput);
    if (this.#syncFrameId !== undefined) cancelAnimationFrame(this.#syncFrameId);
    this.#rangeSliderCleanup?.();
  }

  /**
   * Re-initializes slider bindings when the section HTML is re-rendered.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#scheduleSliderSync();
  }

  /**
   * Schedules slider sync for the next paint after live section updates.
   */
  #scheduleSliderSync() {
    if (this.#syncFrameId !== undefined) cancelAnimationFrame(this.#syncFrameId);

    this.#syncFrameId = requestAnimationFrame(() => {
      this.#syncFrameId = undefined;
      this.#setupRangeSlider();
      this.#syncRangeSliderFromInputs();
      this.#updateSummary();
    });
  }

  /**
   * Safely parses integer-like values that may include separators/spaces.
   * @param {string | null | undefined} value
   * @param {number} fallback
   * @returns {number}
   */
  #parseIntegerLike(value, fallback) {
    if (!value) return fallback;

    const normalized = value.replace(/[\s.,]/g, '');
    const parsed = Number.parseInt(normalized, 10);

    return Number.isFinite(parsed) ? parsed : fallback;
  }

  /**
   * Extracts the placeholder from a money format string, removing currency symbols.
   * @param {string} format - The money format (e.g., "${{amount}}", "{{amount}} USD")
   * @returns {string} Just the placeholder (e.g., "{{amount}}")
   */
  #extractMoneyPlaceholder(format) {
    const match = format.match(/{{\s*\w+\s*}}/);
    return match ? match[0] : '{{amount}}';
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Keeps the visible input values formatted while typing.
   * @param {Event} event
   */
  #onInput = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;

    const { minInput, maxInput } = this.refs;
    if (event.target !== minInput && event.target !== maxInput) return;

    this.#formatDisplayInput(event.target);
    this.#setMinAndMaxValues();
    this.#syncRangeSliderFromInputs();
    this.#updateSummary();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);
    this.#formatDisplayInput(minInput);
    this.#formatDisplayInput(maxInput);
    this.#setMinAndMaxValues();
    this.#syncRangeSliderFromInputs();
    this.#updateSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const previousDisplayValues = this.#setQueryReadyInputValues();
    facetsForm.updateFilters();
    this.#restoreDisplayInputValues(previousDisplayValues);
    this.#setMinAndMaxValues();
    this.#syncRangeSliderFromInputs();
    this.#updateSummary();
  }

  /**
   * Parses a formatted money value into minor units
   * displayValue can come from user input or API response
   * @param {string} displayValue - The display value (e.g., "10.50" for USD, "9,50" for EUR, "1000" for JPY)
   * @param {string} currency - The currency code
   * @returns {number} The value in minor units
   */
  #parseDisplayValue(displayValue, currency) {
    return convertMoneyToMinorUnits(displayValue, currency) ?? 0;
  }

  /**
   * Gets the current input value in minor units, or returns the fallback when empty.
   * @param {HTMLInputElement} input
   * @param {number} fallback
   * @returns {number}
   */
  #getInputValueInMinorUnits(input, fallback) {
    if (input.value.trim() === '') return fallback;
    return this.#parseDisplayValue(input.value, this.currency);
  }

  /**
   * Formats a minor-unit amount for visible inputs.
   * @param {number} value
   * @returns {string}
   */
  #formatMinorValue(value) {
    return formatMoney(value, this.moneyFormat, this.currency).replace(/,/g, '.');
  }

  /**
   * Returns the currency precision used to convert form values.
   * @returns {number}
   */
  #getCurrencyPrecision() {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.currency,
      }).resolvedOptions().maximumFractionDigits;
    } catch {
      return 2;
    }
  }

  /**
   * Converts a minor-unit amount to a query-safe string without thousands separators.
   * @param {number} value
   * @returns {string}
   */
  #formatMinorValueForQuery(value) {
    const precision = this.#getCurrencyPrecision();

    if (precision === 0) return String(value);

    return (value / Math.pow(10, precision)).toFixed(precision).replace(/\.?0+$/, '');
  }

  /**
   * Formats a visible input if it contains a parseable amount.
   * @param {HTMLInputElement} input
   */
  #formatDisplayInput(input) {
    if (input.value.trim() === '') return;

    const parsedValue = this.#parseDisplayValue(input.value, this.currency);
    if (parsedValue === null) return;

    input.value = this.#formatMinorValue(parsedValue);
  }

  /**
   * Replaces visible input values with query-ready values and returns the originals.
   * @returns {{ min: string, max: string }}
   */
  #setQueryReadyInputValues() {
    const { minInput, maxInput } = this.refs;
    const previousValues = { min: minInput.value, max: maxInput.value };
    const rangeElements = this.#getRangeSliderElements();

    if (rangeElements) {
      const currentMin = this.#parseIntegerLike(rangeElements.slider.dataset.valMin, rangeElements.sliderMin);
      const currentMax = this.#parseIntegerLike(rangeElements.slider.dataset.valMax, rangeElements.sliderMax);

      minInput.value = currentMin <= rangeElements.sliderMin
        ? ''
        : this.#formatMinorValueForQuery(currentMin);

      maxInput.value = currentMax >= rangeElements.sliderMax
        ? ''
        : this.#formatMinorValueForQuery(currentMax);

      return previousValues;
    }

    if (minInput.value.trim() !== '') {
      const minValue = this.#parseDisplayValue(minInput.value, this.currency);
      minInput.value = this.#formatMinorValueForQuery(minValue);
    }

    if (maxInput.value.trim() !== '') {
      const maxValue = this.#parseDisplayValue(maxInput.value, this.currency);
      maxInput.value = this.#formatMinorValueForQuery(maxValue);
    }

    return previousValues;
  }

  /**
   * Restores the visible formatted values after the filter submission.
   * @param {{ min: string, max: string }} values
   */
  #restoreDisplayInputValues(values) {
    const { minInput, maxInput } = this.refs;
    minInput.value = values.min;
    maxInput.value = values.max;
  }

  /**
   * Returns the custom slider elements when present.
   * @returns {{
   *   slider: HTMLElement,
   *   fill: HTMLElement,
   *   minThumb: HTMLElement,
   *   maxThumb: HTMLElement,
   *   sliderMin: number,
   *   sliderMax: number,
   * } | null}
   */
  #getRangeSliderElements() {
    const slider = this.querySelector('.price-range-custom');
    const fill = this.querySelector('.price-range-custom__fill');
    const minThumb = this.querySelector('.price-range-custom__thumb--min');
    const maxThumb = this.querySelector('.price-range-custom__thumb--max');

    if (!(slider instanceof HTMLElement)) return null;
    if (!(fill instanceof HTMLElement)) return null;
    if (!(minThumb instanceof HTMLElement)) return null;
    if (!(maxThumb instanceof HTMLElement)) return null;

    const sliderMin = this.#parseIntegerLike(slider.dataset.min, 0);
    const sliderMax = this.#parseIntegerLike(slider.dataset.max, 0);

    return {
      slider,
      fill,
      minThumb,
      maxThumb,
      sliderMin,
      sliderMax,
    };
  }

  /**
   * Updates the slider UI from the current input values.
   */
  #syncRangeSliderFromInputs() {
    const elements = this.#getRangeSliderElements();
    const { minInput, maxInput } = this.refs;

    if (!elements) return;

    const currentMin = this.#getInputValueInMinorUnits(minInput, elements.sliderMin);
    const currentMax = this.#getInputValueInMinorUnits(maxInput, elements.sliderMax);
    this.#renderRangeSlider(elements, currentMin, currentMax);
  }

  /**
   * Renders thumb positions and displayed values.
   * @param {NonNullable<ReturnType<PriceFacetComponent['#getRangeSliderElements']>>} elements
   * @param {number} rawMin
   * @param {number} rawMax
   */
  #renderRangeSlider(elements, rawMin, rawMax) {
    const currentMin = Math.max(elements.sliderMin, Math.min(rawMin, rawMax));
    const currentMax = Math.min(elements.sliderMax, Math.max(rawMax, rawMin));
    const range = Math.max(elements.sliderMax - elements.sliderMin, 1);
    const minPercent = ((currentMin - elements.sliderMin) / range) * 100;
    const maxPercent = ((currentMax - elements.sliderMin) / range) * 100;

    elements.slider.dataset.valMin = String(currentMin);
    elements.slider.dataset.valMax = String(currentMax);
    elements.minThumb.style.left = `${minPercent}%`;
    elements.maxThumb.style.left = `${maxPercent}%`;
    elements.fill.style.left = `${minPercent}%`;
    elements.fill.style.right = `${100 - maxPercent}%`;
    elements.minThumb.setAttribute('aria-valuenow', String(currentMin));
    elements.maxThumb.setAttribute('aria-valuenow', String(currentMax));
  }

  /**
   * Sets the current slider values into the text inputs.
   * @param {number} currentMin
   * @param {number} currentMax
   * @param {number} sliderMin
   * @param {number} sliderMax
   * @param {boolean} shouldSubmit
   */
  #syncInputsFromSlider(currentMin, currentMax, sliderMin, sliderMax, shouldSubmit) {
    const { minInput, maxInput } = this.refs;

    minInput.value = currentMin <= sliderMin ? '' : this.#formatMinorValue(currentMin);
    maxInput.value = currentMax >= sliderMax ? '' : this.#formatMinorValue(currentMax);

    if (shouldSubmit) this.updatePriceFilterAndResults();
  }

  /**
   * Initializes the custom price range slider.
   */
  #setupRangeSlider() {
    this.#rangeSliderCleanup?.();

    const elements = this.#getRangeSliderElements();
    if (!elements) return;

    const { slider, minThumb, maxThumb, sliderMin, sliderMax } = elements;
    const range = Math.max(sliderMax - sliderMin, 1);
    const keyboardStep = Math.max(1, Math.round(range / 100));

    const getCurrentValues = () => ({
      min: this.#parseIntegerLike(slider.dataset.valMin, sliderMin),
      max: this.#parseIntegerLike(slider.dataset.valMax, sliderMax),
    });

    const getValueFromPointer = (clientX) => {
      const rect = slider.getBoundingClientRect();
      const percent = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
      return Math.round(sliderMin + percent * range);
    };

    const updateFromPointer = (clientX, handle) => {
      const nextValue = getValueFromPointer(clientX);
      const currentValues = getCurrentValues();
      const currentMin = handle === 'min' ? Math.min(nextValue, currentValues.max) : currentValues.min;
      const currentMax = handle === 'max' ? Math.max(nextValue, currentValues.min) : currentValues.max;

      this.#renderRangeSlider(elements, currentMin, currentMax);
      this.#syncInputsFromSlider(currentMin, currentMax, sliderMin, sliderMax, false);
      this.#updateSummary();
    };

    const handleThumbPointerDown = (event, handle) => {
      event.preventDefault();

      const thumb = handle === 'min' ? minThumb : maxThumb;
      thumb.setPointerCapture(event.pointerId);
      updateFromPointer(event.clientX, handle);

      const handlePointerMove = (moveEvent) => updateFromPointer(moveEvent.clientX, handle);
      const handlePointerUp = () => {
        thumb.removeEventListener('pointermove', handlePointerMove);
        thumb.removeEventListener('pointerup', handlePointerUp);
        thumb.removeEventListener('pointercancel', handlePointerUp);

        const currentValues = getCurrentValues();
        this.#syncInputsFromSlider(currentValues.min, currentValues.max, sliderMin, sliderMax, true);
      };

      thumb.addEventListener('pointermove', handlePointerMove);
      thumb.addEventListener('pointerup', handlePointerUp);
      thumb.addEventListener('pointercancel', handlePointerUp);
    };

    const handleSliderPointerDown = (event) => {
      if (!(event.target instanceof HTMLElement) || event.target.closest('.price-range-custom__thumb')) return;

      const currentValues = getCurrentValues();
      const nextValue = getValueFromPointer(event.clientX);
      const minDistance = Math.abs(nextValue - currentValues.min);
      const maxDistance = Math.abs(nextValue - currentValues.max);
      const handle = minDistance <= maxDistance ? 'min' : 'max';

      updateFromPointer(event.clientX, handle);

      const updatedValues = getCurrentValues();
      this.#syncInputsFromSlider(updatedValues.min, updatedValues.max, sliderMin, sliderMax, true);
    };

    const handleThumbKeyDown = (event, handle) => {
      const delta = event.key === 'ArrowLeft' || event.key === 'ArrowDown'
        ? -keyboardStep
        : event.key === 'ArrowRight' || event.key === 'ArrowUp'
          ? keyboardStep
          : 0;

      if (!delta) return;

      event.preventDefault();

      const currentValues = getCurrentValues();
      const currentMin = handle === 'min'
        ? Math.max(sliderMin, Math.min(currentValues.min + delta, currentValues.max))
        : currentValues.min;
      const currentMax = handle === 'max'
        ? Math.min(sliderMax, Math.max(currentValues.max + delta, currentValues.min))
        : currentValues.max;

      this.#renderRangeSlider(elements, currentMin, currentMax);
      this.#syncInputsFromSlider(currentMin, currentMax, sliderMin, sliderMax, true);
      this.#updateSummary();
    };

    const handleMinPointerDown = (event) => handleThumbPointerDown(event, 'min');
    const handleMaxPointerDown = (event) => handleThumbPointerDown(event, 'max');
    const handleMinKeyDown = (event) => handleThumbKeyDown(event, 'min');
    const handleMaxKeyDown = (event) => handleThumbKeyDown(event, 'max');

    minThumb.addEventListener('pointerdown', handleMinPointerDown);
    maxThumb.addEventListener('pointerdown', handleMaxPointerDown);
    slider.addEventListener('pointerdown', handleSliderPointerDown);
    minThumb.addEventListener('keydown', handleMinKeyDown);
    maxThumb.addEventListener('keydown', handleMaxKeyDown);

    this.#rangeSliderCleanup = () => {
      minThumb.removeEventListener('pointerdown', handleMinPointerDown);
      maxThumb.removeEventListener('pointerdown', handleMaxPointerDown);
      slider.removeEventListener('pointerdown', handleSliderPointerDown);
      minThumb.removeEventListener('keydown', handleMinKeyDown);
      maxThumb.removeEventListener('keydown', handleMaxKeyDown);
    };
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const { currency } = this;
    // Parse the user's input value using currency-aware parsing
    const value = this.#parseDisplayValue(input.value, currency);

    // data-min and data-max now contain raw minor unit values (not formatted)
    const min = this.#parseDisplayValue(input.getAttribute('data-min') ?? '0', currency);
    const max = this.#parseDisplayValue(input.getAttribute('data-max') ?? '0', currency);

    if (value < min) {
      input.value = this.#formatMinorValue(min);
    } else if (value > max) {
      input.value = this.#formatMinorValue(max);
    }
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const activeClass = this.getAttribute('active-class') || 'active';
      clearButton.classList.toggle(activeClass, event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        const span = document.createElement('span');
        span.className = 'visually-hidden';
        span.textContent = inputElement.getAttribute('aria-label') ?? '';
        return (swatch?.outerHTML ?? '') + span.outerHTML;
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.textContent = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;
    const emptySummary = facetStatus.dataset.emptySummary ?? 'Desde - Hasta';

    if (!minInputValue && !maxInputValue) {
      facetStatus.textContent = emptySummary;
      return;
    }

    const currency = facetStatus.dataset.currency || '';
    const minInputNum = this.#parseCents(minInputValue, '0', currency);
    const maxFallbackMinor = Number.parseInt(facetStatus.dataset.rangeMaxMinor ?? '0', 10);
    const maxInputNum = maxInputValue
      ? this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax, currency)
      : maxFallbackMinor;
    facetStatus.textContent = `${this.#formatMoney(minInputNum)} - ${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as minor units (cents for most currencies, but adjusted for zero-decimal currencies)
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid (formatted string like "11,400")
   * @param {string} currency - The currency code (e.g., 'USD', 'JPY', 'KRW')
   * @returns {number} The money value in minor units
   */
  #parseCents(value, fallback = '0', currency = '') {
    // Try to parse the value
    const result = convertMoneyToMinorUnits(value, currency);
    if (result !== null) return result;

    // Fall back to parsing the fallback string (which may have formatting like "11,400")
    const fallbackResult = convertMoneyToMinorUnits(fallback, currency);
    if (fallbackResult !== null) return fallbackResult;

    // Last resort: clean and parse as integer
    const cleanFallback = fallback.replace(/[^\d]/g, '');
    return parseInt(cleanFallback, 10) || 0;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const format = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return formatMoney(moneyValue, format, currency).replace(/,/g, '.');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.textContent = this.refs.facetStatus.dataset.emptySummary ?? 'Desde - Hasta';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}
